/**
 * Personal data scrubbing for error tracking (#3620, epic #3617).
 *
 * Errors go to OceanSpace's own GlitchTip (errors.oceanspace.fi). They may
 * still carry customer data: a message quoting an email, a plate in a URL,
 * a request body. Everything that leaves a service or a browser passes
 * through `scrubErrorEvent` / `scrubBreadcrumb` (Sentry's `beforeSend` and
 * `beforeBreadcrumb`). No Sentry types here: the shapes are the plain JSON
 * the SDKs hand over, so this module stays dependency-free and runs in both
 * Node and the portals.
 *
 * Masked in every string: email addresses, phone numbers (Finnish and
 * international), Finnish personal identity codes, VINs and Finnish
 * registration plates. Dropped: request and response bodies, cookies, every
 * header but a short allowlist, query parameter values, and any field whose
 * name says it holds a secret. The user is reduced to its id.
 *
 * A provider callback URL (`/api/v1/webhooks/...?k=<capability>`, #3023) is a
 * secret wherever it appears, also inside free text such as an error message,
 * a linked cause or a breadcrumb: `scrubText` keeps its path and masks the
 * query values, so the route stays readable and the capability never leaves.
 * The SMS opt-out link (`/api/v1/webhooks/sms/o/<tenant>/<token>`) carries its
 * capability in the path instead: its token segment is masked the same way.
 */
const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
// +358 40 123 4567, 00358401234567, +1 (555) 123-4567
const INTERNATIONAL_PHONE = /(?<![\w+])(?:\+|00)\d(?:[\s().-]{0,2}\d){6,15}(?!\w)/g;
// 040 123 4567, 040-1234567, 09 123 4567
const FINNISH_PHONE = /(?<![\w+])0\d{1,3}[\s-]?\d{3,4}[\s-]?\d{2,4}(?!\w)/g;
// 131052-308T (henkilötunnus)
const HETU = /(?<![\w])\d{6}[-+A-FUVWXY]\d{3}[0-9A-Y](?!\w)/gi;
// 17 characters without I, O, Q; must mix letters and digits so hex ids pass.
const VIN = /(?<![\w])(?=[A-HJ-NPR-Z0-9]*\d)(?=[A-HJ-NPR-Z0-9]*[A-HJ-NPR-Z])[A-HJ-NPR-Z0-9]{17}(?!\w)/gi;
// ABC-123, AB-1 (Finnish plate)
const FINNISH_PLATE = /(?<![\w-])[A-ZÅÄÖ]{2,3}-\d{1,3}(?![\w-])/gi;
// A webhook URL (full or path-only) with a query, inside any text: the query is the capability.
// A query ends on a value character, so closing punctuation after it (`.`, `,`, `)`) stays in the text.
const WEBHOOK_URL_WITH_QUERY = /(\/api\/v1\/webhooks\/[^\s"'<>?#]*)\?((?:\[x\]|[\w%.~+\-=&;:,@!*$/])*(?:\[x\]|[\w%~+\-=/$]))/gi;
// A secret-named parameter (`k` is the callback capability) in the query of any URL inside text.
const SECRET_QUERY_PARAM = /([?&](?:k|[\w-]*(?:pass(?:word)?|secret|token|auth|cookie|api[-_]?key|session|signature|credential|capabilit)[\w-]*)=)(?:\[x\]|[\w%.~+\-=;:,@!*$/])*(?:\[x\]|[\w%~+\-=/$])/gi;
const SECRET_KEY = /pass(word)?|secret|token|authorization|cookie|api[-_]?key|session|signature|credential/i;
const BODY_KEY = /^(body|data|payload|requestBody|responseBody|request_body|response_body)$/i;
/** Headers kept on a request: enough to read the error, nothing that identifies. */
const HEADER_ALLOWLIST = new Set(['user-agent', 'content-type', 'accept', 'x-request-id']);
const MAX_DEPTH = 8;
// An SMS opt-out link carries its capability as the last PATH segment (`/webhooks/sms/o/<tenant>/<token>`).
const OPT_OUT_TOKEN_IN_PATH = /(\/api\/v1\/webhooks\/sms\/o\/[^\s"'<>/?#]+\/)[\w-]+/gi;
/** `a=1&b=2` → `a=[x]&b=[x]`. */
function maskQueryValues(query) {
    return query
        .split('&')
        .filter((pair) => pair !== '')
        .map((pair) => `${pair.split('=')[0] ?? ''}=[x]`)
        .join('&');
}
/** Masks the callback capability inside a URL embedded in text; the route and other text stay. */
function maskEmbeddedCapabilities(value) {
    return value
        .replace(WEBHOOK_URL_WITH_QUERY, (_match, path, query) => `${path}?${maskQueryValues(query)}`)
        .replace(SECRET_QUERY_PARAM, '$1[x]')
        .replace(OPT_OUT_TOKEN_IN_PATH, '$1[x]');
}
export function scrubText(value) {
    return maskEmbeddedCapabilities(value)
        .replace(EMAIL, '[email]')
        .replace(HETU, '[hetu]')
        .replace(VIN, '[vin]')
        .replace(INTERNATIONAL_PHONE, '[phone]')
        .replace(FINNISH_PHONE, '[phone]')
        .replace(FINNISH_PLATE, '[plate]');
}
/** Masks every query parameter value: `?email=a@b.fi&x=1` → `?email=[x]&x=[x]`. */
export function scrubUrl(value) {
    const q = value.indexOf('?');
    if (q === -1)
        return scrubText(value);
    const hash = value.indexOf('#', q);
    const query = value.slice(q + 1, hash === -1 ? undefined : hash);
    const masked = maskQueryValues(query);
    return `${scrubText(value.slice(0, q))}?${masked}`;
}
/** Deep copy with masked strings, secret- and body-named fields dropped. */
export function scrubValue(value, depth = 0) {
    if (typeof value === 'string')
        return scrubText(value);
    if (value === null || typeof value !== 'object')
        return value;
    if (depth >= MAX_DEPTH)
        return '[depth]';
    if (Array.isArray(value))
        return value.map((item) => scrubValue(item, depth + 1));
    const out = {};
    for (const [key, inner] of Object.entries(value)) {
        if (SECRET_KEY.test(key)) {
            out[key] = '[redacted]';
        }
        else if (BODY_KEY.test(key)) {
            continue;
        }
        else {
            out[key] = scrubValue(inner, depth + 1);
        }
    }
    return out;
}
function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
function scrubRequest(request) {
    const out = {};
    if (typeof request['method'] === 'string')
        out['method'] = request['method'];
    if (typeof request['url'] === 'string')
        out['url'] = scrubUrl(request['url']);
    if (request['query_string'] !== undefined)
        out['query_string'] = '[x]';
    const headers = request['headers'];
    if (isObject(headers)) {
        const kept = {};
        for (const [name, value] of Object.entries(headers)) {
            if (HEADER_ALLOWLIST.has(name.toLowerCase()) && typeof value === 'string') {
                kept[name] = scrubText(value);
            }
        }
        out['headers'] = kept;
    }
    // data, cookies, env and anything else on the request are dropped.
    return out;
}
/**
 * Scrubs a Sentry event in place-safe fashion and returns the copy to send.
 * Returns null never: an event with nothing left is still worth its stack.
 */
export function scrubErrorEvent(event) {
    const source = event;
    const out = {};
    for (const [key, value] of Object.entries(source)) {
        switch (key) {
            case 'request':
                if (isObject(value))
                    out[key] = scrubRequest(value);
                break;
            case 'user':
                if (isObject(value) && (typeof value['id'] === 'string' || typeof value['id'] === 'number')) {
                    out[key] = { id: value['id'] };
                }
                break;
            case 'breadcrumbs':
                if (Array.isArray(value)) {
                    out[key] = value.filter(isObject).map((crumb) => scrubBreadcrumb(crumb));
                }
                break;
            case 'contexts':
                if (isObject(value)) {
                    const contexts = scrubValue(value);
                    if (isObject(contexts['response'])) {
                        const response = contexts['response'];
                        contexts['response'] = { status_code: response['status_code'] };
                    }
                    out[key] = contexts;
                }
                break;
            case 'exception':
            case 'message':
            case 'extra':
            case 'tags':
            case 'logentry':
                out[key] = scrubValue(value);
                break;
            default:
                // Stack frames, sdk, release, environment, ids: no personal data, but
                // frame variables are not collected (no LocalVariables integration).
                out[key] = value;
        }
    }
    return out;
}
export function scrubBreadcrumb(breadcrumb) {
    const source = breadcrumb;
    const out = { ...source };
    if (typeof source['message'] === 'string')
        out['message'] = scrubText(source['message']);
    if (isObject(source['data'])) {
        const data = scrubValue(source['data']);
        if (typeof source['data']['url'] === 'string')
            data['url'] = scrubUrl(source['data']['url']);
        out['data'] = data;
    }
    return out;
}
//# sourceMappingURL=scrub.js.map